// ═══════════════════════════════════════════════════════════════
// Map Engine — Input Handler
// Translates mouse / touch / keyboard into engine mutations.
// ═══════════════════════════════════════════════════════════════

import type { Camera } from "./camera";
import { MIN_ZONE_H, MIN_ZONE_W, SNAP_SIZE } from "./constants";
import { findZoneAt, hitTable, hitZoneHandle, hitZoneHeader } from "./hit-test";
import type { MapTable, MapZone, ResizeHandle } from "./types";

const RESIZE_CURSORS: Record<ResizeHandle, string> = {
  e: "ew-resize",
  n: "ns-resize",
  ne: "nesw-resize",
  nw: "nwse-resize",
  s: "ns-resize",
  se: "nwse-resize",
  sw: "nesw-resize",
  w: "ew-resize",
};

/** Minimum pointer travel (in CSS px) before a touch is treated as a drag.
 *  Below this the touch is still a candidate for tap / long-press. */
const DRAG_THRESHOLD_PX = 8;

/** Duration a touch must be held on a table or zone header to trigger the contextual menu. */
const LONG_PRESS_MS = 500;

/** Disabled tables and zones still emit ``select`` events so the operator can
 *  inspect them, but every edit affordance (drag, resize, connect, dblclick,
 *  contextmenu) is suppressed. Re-enabling happens through Django admin. */
// Policy change (2026-06-04): the editor lets the operator reorganise
// the layout of any zone / table, even when ``isAvailable`` is false.
// "Fuera de servicio" affects reservations and seating flows; it
// should not also freeze the physical layout — the host might want
// to move a closed PATIO's tables while it stays closed for booking.
// The helpers stay so callers don't break, but they always return
// false. Edit gates that surface the "Re-enabling happens through
// Django admin" UI message live in ``MapCanvas.tsx``.
function isTableUneditable(_: MapTable): boolean {
  return false;
}

function isZoneUneditable(_: MapZone): boolean {
  return false;
}

// We import the engine type lazily to avoid circular deps at runtime.
// The engine passes itself as `host` to the constructor.
export interface InputHost {
  readonly canvas: HTMLCanvasElement;
  readonly camera: Camera;
  readonly tables: MapTable[];
  readonly zones: MapZone[];
  readonly isEditMode: boolean;
  /** When true, pan & zoom are disabled (selection still works). */
  readonly mapLocked: boolean;
  connectMode: boolean;
  connectFirstId: string | null;
  selectedTableId: string | null;
  selectedZoneId: string | null;
  hoveredTableId: string | null;

  /** Bring a table to the top of the render order */
  bringTableToFront(table: MapTable): void;
  /** Get all tables assigned to a zone */
  getZoneTables(zoneId: string): MapTable[];
  /** Toggle or create a connection between two tables */
  toggleConnection(idA: string, idB: string): boolean;
  /** Remove a table and its connections */
  removeTable(id: string): void;
  /** Notify that a table was dropped (possibly into a new zone) */
  notifyTableDrop(table: MapTable, fromZone: string | null): void;
  /** Notify that a zone was resized */
  notifyZoneResize(zone: MapZone): void;
  /** Emit a typed event */
  emit(event: string, data?: unknown): void;
  /** Request a re-render */
  requestRender(): void;
  /** Clamp a rect's top-left to the configured world bounds */
  clampToWorld(x: number, y: number, w: number, h: number): { x: number; y: number };
}

function snap(v: number): number {
  return Math.round(v / SNAP_SIZE) * SNAP_SIZE;
}

export class InputHandler {
  private host: InputHost;
  private canvas: HTMLCanvasElement;
  private camera: Camera;

  // Drag state
  private draggingTable: MapTable | null = null;
  private dragOffX = 0;
  private dragOffY = 0;
  private draggingZone: MapZone | null = null;
  private dzOffX = 0;
  private dzOffY = 0;

  // Resize state
  private resizing: {
    zone: MapZone;
    handle: ResizeHandle;
    origX: number;
    origY: number;
    origW: number;
    origH: number;
    startWX: number;
    startWY: number;
  } | null = null;

  // Pan state
  private panning = false;
  private panStartX = 0;
  private panStartY = 0;

  // Touch state
  private lastPinchDist = 0;
  private touchTable: MapTable | null = null;
  private touchZone: MapZone | null = null;
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartClientX = 0;
  private touchStartClientY = 0;
  private touchDragActive = false;
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;

  // Cleanup registry
  private cleanups: (() => void)[] = [];

  constructor(host: InputHost) {
    this.host = host;
    this.canvas = host.canvas;
    this.camera = host.camera;
    this.attach();
  }

  destroy() {
    for (const fn of this.cleanups) {
      fn();
    }
    this.cleanups = [];
  }

  // ── Helpers ─────────────────────────────────────────────────

  private on(
    target: EventTarget,
    event: string,
    handler: EventListener,
    opts?: AddEventListenerOptions,
  ) {
    target.addEventListener(event, handler, opts);
    this.cleanups.push(() => target.removeEventListener(event, handler, opts));
  }

  private setCursor(cursor: string) {
    this.canvas.style.cursor = cursor;
  }

  /** Convert viewport clientX/clientY to canvas-local coordinates */
  private toLocal(clientX: number, clientY: number) {
    const rect = this.canvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  // ── Attach ──────────────────────────────────────────────────

  private attach() {
    this.on(this.canvas, "mousedown", this.onMouseDown);
    this.on(this.canvas, "mousemove", this.onMouseMove);
    this.on(this.canvas, "mouseup", this.onMouseUp);
    this.on(this.canvas, "mouseleave", this.onMouseLeave);
    this.on(this.canvas, "dblclick", this.onDblClick);
    this.on(this.canvas, "wheel", this.onWheel, { passive: false });
    this.on(this.canvas, "contextmenu", this.onContextMenu);
    this.on(this.canvas, "touchstart", this.onTouchStart, { passive: false });
    this.on(this.canvas, "touchmove", this.onTouchMove, { passive: false });
    this.on(this.canvas, "touchend", this.onTouchEnd);
    this.on(window, "keydown", this.onKeyDown);
  }

  // ── Mouse ───────────────────────────────────────────────────

  private onMouseDown = (e: Event) => {
    const me = e as MouseEvent;
    if (me.button === 2) {
      return;
    } // Right-click handled by contextmenu
    const h = this.host;
    const loc = this.toLocal(me.clientX, me.clientY);

    const table = hitTable(h.tables, this.camera, loc.x, loc.y);

    // Connect mode — disabled tables can't be linked from or to.
    if (h.connectMode && h.isEditMode && table) {
      if (isTableUneditable(table)) {
        h.requestRender();
        return;
      }
      if (!h.connectFirstId) {
        h.connectFirstId = table.id;
        h.requestRender();
        return;
      }
      if (h.connectFirstId !== table.id) {
        h.toggleConnection(h.connectFirstId, table.id);
      }
      h.connectFirstId = null;
      h.connectMode = false;
      h.requestRender();
      return;
    }

    // Drag table (edit mode) or just select (view mode). Disabled tables
    // remain selectable for inspection but ignore every edit gesture.
    if (table) {
      h.selectedTableId = table.id;
      h.selectedZoneId = null;
      h.emit("table:select", table);

      if (h.isEditMode && !isTableUneditable(table)) {
        this.draggingTable = table;
        const wp = this.camera.screenToWorld(loc.x, loc.y);
        this.dragOffX = wp.x - table.x;
        this.dragOffY = wp.y - table.y;
        h.bringTableToFront(table);
        this.setCursor("move");
      }
      h.requestRender();
      return;
    }

    // Resize zone handle (edit mode, must check before zone header) — only on
    // editable zones; resize handles also stop drawing in the renderer.
    if (h.isEditMode) {
      const rh = hitZoneHandle(h.zones, this.camera, loc.x, loc.y, h.selectedZoneId);
      if (rh && !isZoneUneditable(rh.zone)) {
        const wp = this.camera.screenToWorld(loc.x, loc.y);
        this.resizing = {
          handle: rh.handle,
          origH: rh.zone.h,
          origW: rh.zone.w,
          origX: rh.zone.x,
          origY: rh.zone.y,
          startWX: wp.x,
          startWY: wp.y,
          zone: rh.zone,
        };
        this.setCursor(RESIZE_CURSORS[rh.handle]);
        return;
      }
    }

    // Drag zone header (edit mode only) — disabled zones still select.
    if (h.isEditMode) {
      const zh = hitZoneHeader(h.zones, this.camera, loc.x, loc.y);
      if (zh) {
        h.selectedZoneId = zh.id;
        h.selectedTableId = null;
        h.emit("zone:select", zh);
        if (!isZoneUneditable(zh)) {
          this.draggingZone = zh;
          const wp = this.camera.screenToWorld(loc.x, loc.y);
          this.dzOffX = wp.x - zh.x;
          this.dzOffY = wp.y - zh.y;
          this.setCursor("move");
        }
        h.requestRender();
        return;
      }
    }

    // Pan (both modes) — skipped when map is locked, but deselect still fires
    // so tapping empty space clears the current selection.
    h.selectedTableId = null;
    h.selectedZoneId = null;
    h.emit("table:select", null);
    h.emit("zone:select", null);
    if (h.mapLocked) {
      h.requestRender();
      return;
    }
    this.panning = true;
    this.panStartX = loc.x - this.camera.offsetX;
    this.panStartY = loc.y - this.camera.offsetY;
    this.setCursor("grabbing");
    h.requestRender();
  };

  private onMouseMove = (e: Event) => {
    const me = e as MouseEvent;
    const h = this.host;
    const loc = this.toLocal(me.clientX, me.clientY);

    // Panning
    if (this.panning) {
      this.camera.offsetX = loc.x - this.panStartX;
      this.camera.offsetY = loc.y - this.panStartY;
      h.requestRender();
      return;
    }

    // Dragging table
    if (this.draggingTable && h.isEditMode) {
      const wp = this.camera.screenToWorld(loc.x, loc.y);
      const t = this.draggingTable;
      const clamped = h.clampToWorld(
        snap(wp.x - this.dragOffX),
        snap(wp.y - this.dragOffY),
        t.w,
        t.h,
      );
      t.x = clamped.x;
      t.y = clamped.y;
      h.requestRender();
      return;
    }

    // Resizing zone
    if (this.resizing) {
      const wp = this.camera.screenToWorld(loc.x, loc.y);
      const dx = snap(wp.x - this.resizing.startWX);
      const dy = snap(wp.y - this.resizing.startWY);
      const { origX, origY, origW, origH, handle, zone } = this.resizing;

      let x = origX,
        y = origY,
        w = origW,
        nh = origH;
      const movesW = handle === "w" || handle === "nw" || handle === "sw";
      const movesE = handle === "e" || handle === "ne" || handle === "se";
      const movesN = handle === "n" || handle === "nw" || handle === "ne";
      const movesS = handle === "s" || handle === "sw" || handle === "se";

      if (movesW) {
        x = origX + dx;
        w = origW - dx;
      }
      if (movesE) {
        w = origW + dx;
      }
      if (movesN) {
        y = origY + dy;
        nh = origH - dy;
      }
      if (movesS) {
        nh = origH + dy;
      }

      if (w < MIN_ZONE_W) {
        if (movesW) {
          x = origX + origW - MIN_ZONE_W;
        }
        w = MIN_ZONE_W;
      }
      if (nh < MIN_ZONE_H) {
        if (movesN) {
          y = origY + origH - MIN_ZONE_H;
        }
        nh = MIN_ZONE_H;
      }

      const clampedZone = h.clampToWorld(x, y, w, nh);
      zone.x = clampedZone.x;
      zone.y = clampedZone.y;
      zone.w = w;
      zone.h = nh;
      h.requestRender();
      return;
    }

    // Dragging zone (moves zone + its tables)
    if (this.draggingZone && h.isEditMode) {
      const wp = this.camera.screenToWorld(loc.x, loc.y);
      const z = this.draggingZone;
      const target = h.clampToWorld(snap(wp.x - this.dzOffX), snap(wp.y - this.dzOffY), z.w, z.h);
      const dx = target.x - z.x;
      const dy = target.y - z.y;
      for (const t of h.getZoneTables(z.id)) {
        const tc = h.clampToWorld(t.x + dx, t.y + dy, t.w, t.h);
        t.x = tc.x;
        t.y = tc.y;
      }
      z.x = target.x;
      z.y = target.y;
      h.requestRender();
      return;
    }

    // Hover detection
    const table = hitTable(h.tables, this.camera, loc.x, loc.y);
    const newHover = table ? table.id : null;

    if (newHover !== h.hoveredTableId) {
      h.hoveredTableId = newHover;
      h.requestRender();
    }

    // Cursor: resize handle > table > zone header > connect > default
    if (h.isEditMode) {
      const rh = hitZoneHandle(h.zones, this.camera, loc.x, loc.y, h.selectedZoneId);
      if (rh) {
        this.setCursor(RESIZE_CURSORS[rh.handle]);
        return;
      }
    }
    const zh = hitZoneHeader(h.zones, this.camera, loc.x, loc.y);
    this.setCursor(table ? "pointer" : zh ? "move" : h.connectMode ? "crosshair" : "grab");
  };

  private onMouseUp = () => {
    const h = this.host;

    // Finalize zone resize
    if (this.resizing) {
      h.notifyZoneResize(this.resizing.zone);
      this.resizing = null;
      this.setCursor("grab");
      h.requestRender();
      return;
    }

    // Finalize table drag — always save position, auto-assign zone
    if (this.draggingTable && h.isEditMode) {
      const t = this.draggingTable;
      const fromZone = t.zone;
      const center = findZoneAt(h.zones, t.x + t.w / 2, t.y + t.h / 2);
      t.zone = center ? center.id : null;
      h.notifyTableDrop(t, fromZone);
    }

    // Finalize zone drag — save position
    if (this.draggingZone && h.isEditMode) {
      h.notifyZoneResize(this.draggingZone);
    }

    this.draggingTable = null;
    this.draggingZone = null;
    this.panning = false;
    this.setCursor("grab");
    h.requestRender();
  };

  private onMouseLeave = () => {
    this.draggingTable = null;
    this.draggingZone = null;
    this.resizing = null;
    this.panning = false;
    this.host.hoveredTableId = null;
    this.setCursor("grab");
    this.host.requestRender();
  };

  // ── Double-click ────────────────────────────────────────────

  private onDblClick = (e: Event) => {
    const me = e as MouseEvent;
    const h = this.host;
    const loc = this.toLocal(me.clientX, me.clientY);

    const table = hitTable(h.tables, this.camera, loc.x, loc.y);
    if (table) {
      h.selectedTableId = table.id;
      // Disabled tables don't open the edit panel — re-enable via Django admin.
      if (!isTableUneditable(table)) {
        h.emit("table:dblclick", table);
      }
      h.requestRender();
      return;
    }

    if (h.isEditMode) {
      const zh = hitZoneHeader(h.zones, this.camera, loc.x, loc.y);
      if (zh && !isZoneUneditable(zh)) {
        h.selectedZoneId = zh.id;
        h.emit("zone:dblclick", zh);
        h.requestRender();
      }
    }
  };

  // ── Wheel (zoom) ────────────────────────────────────────────

  private onWheel = (e: Event) => {
    const we = e as WheelEvent;
    we.preventDefault();
    if (this.host.mapLocked) return;
    const loc = this.toLocal(we.clientX, we.clientY);
    this.camera.zoom(loc.x, loc.y, we.deltaY);
    this.host.emit("zoom", this.camera.zoomPercent);
    this.host.requestRender();
  };

  // ── Context menu ────────────────────────────────────────────

  private onContextMenu = (e: Event) => {
    const me = e as MouseEvent;
    me.preventDefault();
    const h = this.host;
    const loc = this.toLocal(me.clientX, me.clientY);
    const table = hitTable(h.tables, this.camera, loc.x, loc.y);
    if (table) {
      h.selectedTableId = table.id;
      // Emit viewport coords for CSS position:fixed context menu. Disabled
      // tables still emit so the view-mode handler can surface a "no
      // disponible" toast; the edit-mode UI is responsible for suppressing
      // the menu render.
      h.emit("table:contextmenu", { table, x: me.clientX, y: me.clientY });
      h.requestRender();
      return;
    }
    // Zone header right-click (edit mode only, mirrors table:contextmenu).
    // Zone contextmenu only carries edit actions, so we skip it on disabled
    // zones at the engine level.
    if (h.isEditMode) {
      const zh = hitZoneHeader(h.zones, this.camera, loc.x, loc.y);
      if (zh && !isZoneUneditable(zh)) {
        h.selectedZoneId = zh.id;
        h.emit("zone:contextmenu", { zone: zh, x: me.clientX, y: me.clientY });
        h.requestRender();
      }
    }
  };

  // ── Touch ───────────────────────────────────────────────────

  private onTouchStart = (e: Event) => {
    const te = e as TouchEvent;
    te.preventDefault();
    const h = this.host;

    if (te.touches.length === 1) {
      const tc = te.touches[0];
      const loc = this.toLocal(tc.clientX, tc.clientY);
      const table = hitTable(h.tables, this.camera, loc.x, loc.y);

      // Record the initial touch position so onTouchMove can measure travel
      // distance against DRAG_THRESHOLD_PX before promoting to a drag / cancelling
      // the long-press timer.
      this.touchStartX = loc.x;
      this.touchStartY = loc.y;
      this.touchStartClientX = tc.clientX;
      this.touchStartClientY = tc.clientY;
      this.touchDragActive = false;

      if (table) {
        // Connect mode: first tap selects, second tap toggles connection.
        // Mirrors onMouseDown so touch devices can connect tables too. A
        // disabled table can't be a source or target.
        if (h.connectMode && h.isEditMode) {
          if (isTableUneditable(table)) {
            h.requestRender();
            return;
          }
          if (!h.connectFirstId) {
            h.connectFirstId = table.id;
            h.requestRender();
            return;
          }
          if (h.connectFirstId !== table.id) {
            h.toggleConnection(h.connectFirstId, table.id);
          }
          h.connectFirstId = null;
          h.connectMode = false;
          h.requestRender();
          return;
        }

        h.selectedTableId = table.id;
        h.emit("table:select", table);
        if (h.isEditMode && !isTableUneditable(table)) {
          this.touchTable = table;
          const wp = this.camera.screenToWorld(loc.x, loc.y);
          this.dragOffX = wp.x - table.x;
          this.dragOffY = wp.y - table.y;
          h.bringTableToFront(table);
        }
        // Long-press emits ``table:contextmenu``. Consumers are responsible
        // for showing or suppressing the menu — view-mode surfaces a toast
        // for disabled tables; edit-mode skips the menu render.
        this.scheduleLongPress({ kind: "table", target: table });
      } else if (h.isEditMode) {
        // Resize handle on the currently-selected zone — must come before
        // the header check so dragging a corner resizes instead of moving
        // the whole zone.
        const rh = hitZoneHandle(h.zones, this.camera, loc.x, loc.y, h.selectedZoneId);
        if (rh && !isZoneUneditable(rh.zone)) {
          const wp = this.camera.screenToWorld(loc.x, loc.y);
          this.resizing = {
            handle: rh.handle,
            origH: rh.zone.h,
            origW: rh.zone.w,
            origX: rh.zone.x,
            origY: rh.zone.y,
            startWX: wp.x,
            startWY: wp.y,
            zone: rh.zone,
          };
          this.touchDragActive = true;
          h.requestRender();
          return;
        }
        const zh = hitZoneHeader(h.zones, this.camera, loc.x, loc.y);
        if (zh) {
          h.selectedZoneId = zh.id;
          h.emit("zone:select", zh);
          if (!isZoneUneditable(zh)) {
            this.touchZone = zh;
            const wp = this.camera.screenToWorld(loc.x, loc.y);
            this.dzOffX = wp.x - zh.x;
            this.dzOffY = wp.y - zh.y;
            // Long-press on zone header → zone:contextmenu (same UX as tables)
            this.scheduleLongPress({ kind: "zone", target: zh });
          }
        } else {
          this.startPanFromTouch(tc);
        }
      } else {
        this.startPanFromTouch(tc);
      }
      h.requestRender();
    }

    // Pinch zoom init. A second finger landing means the user is zooming —
    // cancel any pending long-press so we don't fire the contextual menu.
    if (te.touches.length === 2 && !h.mapLocked) {
      this.cancelLongPress();
      this.lastPinchDist = this.touchDistance(te.touches);
    }
  };

  private onTouchMove = (e: Event) => {
    const te = e as TouchEvent;
    te.preventDefault();
    const h = this.host;

    if (te.touches.length === 1) {
      const tc = te.touches[0];
      const loc = this.toLocal(tc.clientX, tc.clientY);

      // Resizing zone — runs before the drag-threshold gate because it was
      // started on a deliberate handle press in onTouchStart.
      if (this.resizing && h.isEditMode) {
        const wp = this.camera.screenToWorld(loc.x, loc.y);
        const dx = snap(wp.x - this.resizing.startWX);
        const dy = snap(wp.y - this.resizing.startWY);
        const { origX, origY, origW, origH, handle, zone } = this.resizing;

        let x = origX;
        let y = origY;
        let w = origW;
        let nh = origH;
        const movesW = handle === "w" || handle === "nw" || handle === "sw";
        const movesE = handle === "e" || handle === "ne" || handle === "se";
        const movesN = handle === "n" || handle === "nw" || handle === "ne";
        const movesS = handle === "s" || handle === "sw" || handle === "se";

        if (movesW) {
          x = origX + dx;
          w = origW - dx;
        }
        if (movesE) {
          w = origW + dx;
        }
        if (movesN) {
          y = origY + dy;
          nh = origH - dy;
        }
        if (movesS) {
          nh = origH + dy;
        }

        if (w < MIN_ZONE_W) {
          if (movesW) x = origX + origW - MIN_ZONE_W;
          w = MIN_ZONE_W;
        }
        if (nh < MIN_ZONE_H) {
          if (movesN) y = origY + origH - MIN_ZONE_H;
          nh = MIN_ZONE_H;
        }

        const clampedZone = h.clampToWorld(x, y, w, nh);
        zone.x = clampedZone.x;
        zone.y = clampedZone.y;
        zone.w = w;
        zone.h = nh;
        h.requestRender();
        return;
      }

      // Promote the touch to a drag only once it has crossed DRAG_THRESHOLD_PX.
      // Below the threshold the touch is still eligible for tap or long-press,
      // so we neither move the table nor the zone.
      if (!this.touchDragActive) {
        const dx = loc.x - this.touchStartX;
        const dy = loc.y - this.touchStartY;
        if (dx * dx + dy * dy >= DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) {
          this.touchDragActive = true;
          this.cancelLongPress();
        } else {
          return;
        }
      }

      if (this.touchTable && h.isEditMode) {
        const wp = this.camera.screenToWorld(loc.x, loc.y);
        const t = this.touchTable;
        const clamped = h.clampToWorld(
          snap(wp.x - this.dragOffX),
          snap(wp.y - this.dragOffY),
          t.w,
          t.h,
        );
        t.x = clamped.x;
        t.y = clamped.y;
      } else if (this.touchZone && h.isEditMode) {
        const wp = this.camera.screenToWorld(loc.x, loc.y);
        const z = this.touchZone;
        const target = h.clampToWorld(snap(wp.x - this.dzOffX), snap(wp.y - this.dzOffY), z.w, z.h);
        const dx = target.x - z.x;
        const dy = target.y - z.y;
        for (const t of h.getZoneTables(z.id)) {
          const tc = h.clampToWorld(t.x + dx, t.y + dy, t.w, t.h);
          t.x = tc.x;
          t.y = tc.y;
        }
        z.x = target.x;
        z.y = target.y;
      } else if (this.panning) {
        this.camera.offsetX = loc.x - this.panStartX;
        this.camera.offsetY = loc.y - this.panStartY;
      }
      h.requestRender();
    }

    // Pinch zoom
    if (te.touches.length === 2 && this.lastPinchDist > 0) {
      const dist = this.touchDistance(te.touches);
      const mid = this.toLocal(
        (te.touches[0].clientX + te.touches[1].clientX) / 2,
        (te.touches[0].clientY + te.touches[1].clientY) / 2,
      );
      this.camera.pinchZoom(mid.x, mid.y, dist / this.lastPinchDist);
      this.lastPinchDist = dist;
      h.emit("zoom", this.camera.zoomPercent);
      h.requestRender();
    }
  };

  private onTouchEnd = () => {
    const h = this.host;

    // Finalize zone resize — same as the mouse path.
    if (this.resizing && h.isEditMode) {
      h.notifyZoneResize(this.resizing.zone);
      this.resizing = null;
    }

    // Finalize table drag — always emit drop so the new position persists,
    // even when the table stayed inside the same zone. The drag-active flag
    // distinguishes a real move from a tap/long-press.
    if (this.touchTable && h.isEditMode && this.touchDragActive) {
      const t = this.touchTable;
      const fromZone = t.zone;
      const center = findZoneAt(h.zones, t.x + t.w / 2, t.y + t.h / 2);
      t.zone = center ? center.id : null;
      h.notifyTableDrop(t, fromZone);
    }

    // Finalize zone drag — mirror the mouse-up branch so touch-driven zone
    // moves persist too.
    if (this.touchZone && h.isEditMode && this.touchDragActive) {
      h.notifyZoneResize(this.touchZone);
    }

    this.cancelLongPress();
    this.touchTable = null;
    this.touchZone = null;
    this.panning = false;
    this.lastPinchDist = 0;
    this.touchDragActive = false;
    h.requestRender();
  };

  // ── Long-press helpers ──────────────────────────────────────

  private scheduleLongPress(
    payload: { kind: "table"; target: MapTable } | { kind: "zone"; target: MapZone },
  ) {
    this.cancelLongPress();
    this.longPressTimer = setTimeout(() => {
      this.longPressTimer = null;
      if (payload.kind === "table") {
        this.host.emit("table:contextmenu", {
          table: payload.target,
          x: this.touchStartClientX,
          y: this.touchStartClientY,
        });
      } else {
        this.host.emit("zone:contextmenu", {
          zone: payload.target,
          x: this.touchStartClientX,
          y: this.touchStartClientY,
        });
      }
    }, LONG_PRESS_MS);
  }

  private cancelLongPress() {
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  // ── Keyboard ────────────────────────────────────────────────

  private onKeyDown = (e: Event) => {
    const ke = e as KeyboardEvent;
    const h = this.host;

    if (ke.key === "Escape") {
      h.selectedTableId = null;
      h.selectedZoneId = null;
      h.connectMode = false;
      h.connectFirstId = null;
      h.emit("table:select", null);
      h.emit("zone:select", null);
      h.requestRender();
      return;
    }

    // Delete table (edit mode, no input focused)
    if (
      (ke.key === "Delete" || ke.key === "Backspace") &&
      h.isEditMode &&
      h.selectedTableId !== null &&
      !this.isInputFocused()
    ) {
      h.removeTable(h.selectedTableId);
      h.selectedTableId = null;
      h.emit("table:select", null);
      h.requestRender();
    }
  };

  // ── Utils ───────────────────────────────────────────────────

  private startPanFromTouch(tc: Touch) {
    this.host.selectedTableId = null;
    this.host.emit("table:select", null);
    if (this.host.mapLocked) return;
    const loc = this.toLocal(tc.clientX, tc.clientY);
    this.panning = true;
    this.panStartX = loc.x - this.camera.offsetX;
    this.panStartY = loc.y - this.camera.offsetY;
  }

  private touchDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private isInputFocused(): boolean {
    const el = document.activeElement;
    return (
      el instanceof HTMLInputElement ||
      el instanceof HTMLSelectElement ||
      el instanceof HTMLTextAreaElement
    );
  }
}
